import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logoImage from '../../assets/Images/momentum-amp-logo.svg';
import logoMark from '../../assets/Images/momentum-amp-mark.png';
import {
  getAuthUser, logout as clearSession,
  isCarrierAdmin, isCarrierSubUser, canManageCarrierUsers,
} from '../../services/authApi';

interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, isCollapsed, onClose, onToggleCollapse }) => {
  const navigate = useNavigate();
  const user = getAuthUser();
  const isCarrier = !!user?.isCarrierUser;
  const [userName, setUserName] = useState('Underwriter');

  useEffect(() => {
    if (user?.fullName) {
      setUserName(user.fullName);
      return;
    }
    const email = sessionStorage.getItem('userEmail');
    if (email) {
      const namePart = email.split('@')[0];
      const formatted = namePart
        .split(/[._-]/)
        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      setUserName(formatted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carrier users get their own menu. NowCerts users keep the original one,
  // unchanged, so the existing MTM flow is untouched.
  let navItems: Array<{ path: string; label: string; icon: string; badge?: number; end?: boolean }> = [
    { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
    // { path: '/new-quote', label: 'New Quote', icon: 'quote' },
    { path: '/quote-history', label: 'Quote History', icon: 'history', badge: 4 },
    // { path: '/clients', label: 'Clients', icon: 'clients' },
  ];

  if (isCarrierAdmin(user)) {
    navItems = [
      { path: '/carrier', label: 'Dashboard', icon: 'dashboard', end: true },
    ];
    if (canManageCarrierUsers(user)) {
      navItems.push({ path: '/carrier/users', label: 'Users', icon: 'clients' });
    }
    navItems.push({ path: '/carrier/underwriting-inbox', label: 'Underwriting Inbox', icon: 'truck' });
  } else if (isCarrierSubUser(user)) {
    navItems = [
      { path: '/my-quotes', label: 'Dashboard', icon: 'dashboard' },
    ];
  }

  const handleLogout = () => {
    clearSession();
    navigate('/login');
  };

  const renderIcon = (icon: string) => {
    switch (icon) {
      case 'dashboard':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
          </svg>
        );
      case 'quote':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" />
            <line x1="9" y1="15" x2="15" y2="15" />
          </svg>
        );
      case 'history':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        );
      case 'clients':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        );
      case 'truck':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="15" height="13" />
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
            <circle cx="5.5" cy="18.5" r="2.5" />
            <circle cx="18.5" cy="18.5" r="2.5" />
          </svg>
        );
      default:
        return null;
    }
  };

  const sidebarVariants = {
    expanded: { width: 240 },
    collapsed: { width: 72 }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="sidebar__overlay sidebar__overlay--visible"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
        )}
      </AnimatePresence>
      
      <motion.aside 
        className={`sidebar ${isOpen ? 'sidebar--open' : ''} ${isCollapsed ? 'sidebar--collapsed' : ''}${isCarrier ? ' sidebar--carrier' : ''}`}
        initial={false}
        animate={isCollapsed ? 'collapsed' : 'expanded'}
        variants={sidebarVariants}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        <div className="sidebar__logo">
          {/* Collapsed carrier rail only — the wordmark can't read at 72px */}
          <img
            src={isCarrier && isCollapsed ? logoMark : logoImage}
            alt="Momentum AMP"
            className={isCarrier && isCollapsed ? 'sidebar__logo-mark' : 'sidebar__logo-image'}
          />
          <motion.button
            className="sidebar__collapse"
            onClick={onToggleCollapse}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </motion.button>
        </div>


        <nav className="sidebar__nav">
          <AnimatePresence>
            {!isCollapsed && (
              <motion.div
                className="sidebar__nav-section-label"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                Menu
              </motion.div>
            )}
          </AnimatePresence>
          <ul className="sidebar__nav-list">
            {navItems.map((item, index) => (
              <motion.li 
                key={item.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05, duration: 0.2 }}
              >
                <NavLink
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    `sidebar__nav-item ${isActive ? 'sidebar__nav-item--active' : ''}`
                  }
                  onClick={onClose}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span className="sidebar__nav-icon">{renderIcon(item.icon)}</span>
                  <AnimatePresence>
                    {!isCollapsed && (
                      <motion.span 
                        className="sidebar__nav-label"
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {item.badge && !isCollapsed && (
                    <motion.span 
                      className="sidebar__nav-badge"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring' }}
                    >
                      {item.badge}
                    </motion.span>
                  )}
                </NavLink>
              </motion.li>
            ))}
          </ul>
        </nav>

        <div className="sidebar__bottom">
          {/* User profile card */}
          <div className="sidebar__user-card" title={isCollapsed ? userName : undefined}>
            <div className="sidebar__user-avatar">
              {user?.avatarName || localStorage.getItem('AvatarName') || 'U'}
            </div>
            <AnimatePresence>
              {!isCollapsed && (
                <motion.div
                  className="sidebar__user-details"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <span className="sidebar__user-name">{userName}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Logout */}
          <motion.button
            className="sidebar__logout"
            onClick={handleLogout}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            aria-label="Logout"
          >
            <span className="sidebar__logout-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </span>
            <AnimatePresence>
              {!isCollapsed && (
                <motion.span
                  className="sidebar__logout-label"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar;
